import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';
import BlogCTA from '../../components/BlogCTA';
import RelatedArticles from '../../components/RelatedArticles';

const schema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Article',
      'headline': 'How to Invoice as Self-Employed — What to Include and How to Get Paid',
      'description': 'A complete guide to invoicing as a self-employed person or sole trader — what fields are required, how to number your invoices, what payment terms to use, and how to get paid faster.',
      'url': 'https://www.invilic.com/blog/self-employed-invoice-guide',
      'datePublished': '2026-06-01',
      'dateModified': '2026-06-01',
      'author': { '@type': 'Organization', 'name': 'Invilic', 'url': 'https://www.invilic.com' },
      'publisher': { '@type': 'Organization', 'name': 'Invilic', 'url': 'https://www.invilic.com' },
    },
    {
      '@type': 'FAQPage',
      'mainEntity': [
        {
          '@type': 'Question',
          'name': 'What do I need to put on an invoice as self-employed?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'A self-employed invoice should include: your name (or trading name) and contact details, the client\'s name and address, a unique invoice number, the invoice date, a description of the work done, the amount charged for each item, the total amount due, your payment terms (e.g. Net 30), and your bank details. If you are VAT-registered, also include your VAT number and VAT breakdown.' },
        },
        {
          '@type': 'Question',
          'name': 'Do self-employed people need to charge VAT?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'Not unless you are VAT-registered. In the UK, you must register for VAT if your taxable turnover exceeds £90,000 in any 12-month rolling period. Below this threshold, you do not charge VAT and do not need a VAT number. In Australia, the threshold is A$75,000 annual turnover. Do not add a VAT/GST line to your invoice if you are not registered — this is a legal offence.' },
        },
        {
          '@type': 'Question',
          'name': 'How should I number my self-employed invoices?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'Use a simple sequential system. Start with INV-001 or INV-2026-001 and increment by one for each invoice. Never reuse or skip numbers. Resetting per calendar or financial year is common and acceptable — just include the year in the number so old and new INV-001 invoices are distinguishable. Most invoice tools, including Invilic, generate these numbers automatically.' },
        },
        {
          '@type': 'Question',
          'name': 'What payment terms should a self-employed person use?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'Most self-employed people use Net 15 or Net 30 as standard. For new clients, consider "Due on Receipt" or request a 50% deposit upfront with the balance on completion. Net 15 is better for cash flow; Net 30 is more acceptable to corporate clients. Always put payment terms on every invoice — a missing deadline is a late payment waiting to happen.' },
        },
        {
          '@type': 'Question',
          'name': 'Do self-employed people need a business bank account to invoice?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'Legally, sole traders in the UK and Australia do not need a separate business bank account. You can invoice with your personal bank details. However, a dedicated business account makes bookkeeping significantly easier and looks more professional to clients. Most banks offer free or low-cost business accounts for sole traders.' },
        },
      ],
    },
  ],
};

const faqs = schema['@graph'][1].mainEntity;

const invoiceFields = [
  { field: 'Your name or trading name', note: 'Sole traders: use your legal name or your registered trading name' },
  { field: 'Your contact details', note: 'Email address and optionally phone number and address' },
  { field: "Client's name and address", note: 'Full name and business address of who you are billing' },
  { field: 'A unique invoice number', note: 'e.g. INV-2026-001 — never repeat or skip numbers' },
  { field: 'Invoice date', note: 'The date you are issuing the invoice' },
  { field: 'Description of work completed', note: 'Be specific enough that the client knows exactly what they are paying for' },
  { field: 'Amount per item or service', note: 'Hourly rate × hours, or project fee — itemise where possible' },
  { field: 'Total amount due', note: 'After any discounts or adjustments' },
  { field: 'Payment terms', note: 'e.g. "Payment due within 30 days" — never leave this blank' },
  { field: 'Bank details or payment instructions', note: 'Sort code + account number, or PayPal/Stripe link' },
];

const tipsToGetPaid = [
  { tip: 'Invoice the same day you finish the work', detail: 'The longer you wait, the less psychologically urgent the payment feels to the client.' },
  { tip: 'Include the exact due date, not just "Net 30"', detail: 'Writing "due by 1 July 2026" removes the need for the client to do date maths.' },
  { tip: 'Make it easy to pay', detail: 'Include your bank details, a PayPal link, or a payment link directly on the invoice.' },
  { tip: 'Send a polite reminder 2 days before the due date', detail: 'Most late payments are forgotten, not deliberate. A brief "just a reminder" email resolves most cases.' },
  { tip: 'Follow up immediately on the day after the deadline', detail: "Don't wait a week. Prompt follow-up signals that you take payment seriously." },
  { tip: 'State your late fee policy upfront', detail: 'Even if you never enforce it, stating "1.5% monthly interest on overdue balances" encourages faster payment.' },
];

export default function SelfEmployedInvoicePage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="How to Invoice as Self-Employed — What to Include and How to Get Paid | Invilic Blog"
        description="Complete self-employed invoicing guide: what fields to include, how to number invoices, what payment terms to use, VAT rules, and tips to get paid faster."
        canonical="https://www.invilic.com/blog/self-employed-invoice-guide"
        ogType="article"
        schema={schema}
      />
      <SeoNav />

      <main className="max-w-3xl mx-auto px-6 py-16">
        <header className="mb-10">
          <div className="flex items-center gap-2 mb-5 text-sm">
            <Link to="/blog" className="text-blue-600 dark:text-blue-400 hover:underline">Blog</Link>
            <ArrowRight className="w-3 h-3 text-gray-400" />
            <span className="text-gray-500 dark:text-gray-400">Freelancing</span>
          </div>
          <span className="inline-block bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-semibold px-2.5 py-1 rounded-full mb-4">Freelancing</span>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 dark:text-white leading-tight mb-4">
            How to Invoice as Self-Employed — What to Include and How to Get Paid
          </h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm">June 1, 2026 · 6 min read</p>
        </header>

        <article className="text-gray-700 dark:text-gray-300 leading-relaxed">

          <p className="text-lg mb-6">
            Invoicing as a self-employed person or sole trader does not require specialist software or a business degree. You need a document that clearly states who you are, what you did, how much you are owed, and when you expect to be paid. Get those four things right and you will get paid faster and have clean records when tax season arrives.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">What to Include on a Self-Employed Invoice</h2>
          <p className="mb-4">Every self-employed invoice should contain these fields:</p>
          <div className="space-y-3 mb-8">
            {invoiceFields.map(({ field, note }, i) => (
              <div key={field} className="flex items-start gap-3">
                <div className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 flex items-center justify-center text-xs font-bold mt-0.5">{i + 1}</div>
                <div className="text-sm">
                  <span className="font-medium text-gray-900 dark:text-white">{field}</span>
                  {note && <p className="text-gray-400 dark:text-gray-500 mt-0.5">{note}</p>}
                </div>
              </div>
            ))}
          </div>

          <BlogCTA
            text="Create a professional self-employed invoice in minutes — free, no account needed, PDF download."
            to="/self-employed-invoice-template"
            btnText="Create Invoice Free"
          />

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">VAT — Do You Need to Charge It?</h2>
          <p className="mb-4">
            Most self-employed people starting out do not need to charge VAT. The rules vary by country:
          </p>
          <div className="space-y-3 mb-6">
            {[
              { country: 'United Kingdom', rule: 'Register for VAT when your taxable turnover exceeds £90,000 in any 12-month rolling period. Below this, do not charge VAT. Voluntary registration is possible below the threshold.' },
              { country: 'Australia', rule: 'Register for GST when your annual turnover reaches A$75,000. Below this, issue regular invoices without GST. Ride-share and taxi drivers must register regardless of turnover.' },
              { country: 'United States', rule: 'No federal VAT/GST. Sales tax is state-level and applies to goods, not most services. Most self-employed service providers do not need to collect sales tax.' },
              { country: 'European Union', rule: 'VAT thresholds vary by member state — typically €35,000–€85,000. Check your country\'s specific threshold. Cross-border B2B services use reverse charge.' },
            ].map(({ country, rule }) => (
              <div key={country} className="p-4 border border-gray-100 dark:border-gray-800 rounded-xl text-sm">
                <p className="font-semibold text-gray-900 dark:text-white mb-1">{country}</p>
                <p className="text-gray-500 dark:text-gray-400">{rule}</p>
              </div>
            ))}
          </div>
          <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800/30 rounded-xl mb-8 text-sm">
            <strong className="font-semibold text-gray-900 dark:text-white">Important:</strong> Never add a VAT or GST line to your invoice if you are not registered. Collecting tax you are not authorised to collect is a legal offence in all of these jurisdictions.
          </div>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">How to Number Your Invoices</h2>
          <p className="mb-4">
            Use a simple sequential system starting from 001. The most common format is <span className="font-mono bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded text-sm">INV-2026-001</span>, incrementing by one for each new invoice.
          </p>
          <ul className="space-y-2 mb-8 text-sm">
            {[
              'Never reuse a number — even for a cancelled invoice (void it instead)',
              'Never skip numbers — gaps look suspicious to HMRC/ATO',
              'Resetting per year is fine — just include the year so INV-2025-001 and INV-2026-001 are distinguishable',
              'Keep the format consistent — choose one style and stick with it',
            ].map(item => (
              <li key={item} className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                <span>{item}</span>
              </li>
            ))}
          </ul>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">What Payment Terms Should You Use?</h2>
          <p className="mb-4">Your payment terms determine when you get paid. Common options:</p>
          <div className="space-y-3 mb-8">
            {[
              { term: 'Due upon receipt', when: 'New clients, small one-off jobs, high-risk situations', note: 'Payment expected immediately — fastest for cash flow' },
              { term: 'Net 15', when: 'Established freelance clients, ongoing work', note: 'Good balance of cash flow and professionalism' },
              { term: 'Net 30', when: 'Corporate clients, large project invoices', note: 'Industry standard for B2B — less friction with big clients' },
              { term: '50% deposit + balance on completion', when: 'New clients, large projects', note: 'Best protection against non-payment for significant work' },
            ].map(({ term, when, note }) => (
              <div key={term} className="border border-gray-100 dark:border-gray-800 rounded-xl p-4 text-sm">
                <p className="font-bold text-gray-900 dark:text-white mb-1">{term}</p>
                <p className="text-gray-500 dark:text-gray-400 mb-1"><strong className="text-gray-700 dark:text-gray-300">Use when:</strong> {when}</p>
                <p className="text-gray-400 dark:text-gray-500">{note}</p>
              </div>
            ))}
          </div>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">6 Tips to Get Paid Faster</h2>
          <div className="space-y-4 mb-8">
            {tipsToGetPaid.map(({ tip, detail }) => (
              <div key={tip} className="flex items-start gap-3 text-sm">
                <span className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0 mt-1.5" />
                <div>
                  <p className="font-semibold text-gray-900 dark:text-white">{tip}</p>
                  <p className="text-gray-500 dark:text-gray-400 mt-0.5">{detail}</p>
                </div>
              </div>
            ))}
          </div>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">Record-Keeping for Self-Employed Workers</h2>
          <p className="mb-4">
            Keep copies of every invoice you send, whether paid or unpaid. Requirements by country:
          </p>
          <ul className="space-y-2 mb-8 text-sm">
            {[
              'UK: Keep business records for 5 years after the 31 January self-assessment deadline (usually 5–6 years total)',
              'Australia: Keep tax records for 5 years from when the return was lodged',
              'US: IRS recommends keeping supporting documents for 3–7 years depending on the situation',
            ].map(item => (
              <li key={item} className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0 mt-2" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <p className="mb-8 text-sm">
            Store invoices in a folder organised by year and month. PDF copies are acceptable in all jurisdictions.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-6">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqs.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-5 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none text-sm">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-5 pb-4 text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{acceptedAnswer.text}</p>
              </details>
            ))}
          </div>

          <BlogCTA
            text="Create a self-employed invoice in 2 minutes — sequential numbering, payment terms, professional PDF. Free forever."
            to="/self-employed-invoice-template"
            btnText="Create Invoice Free"
          />

        </article>
        <RelatedArticles current="/blog/self-employed-invoice-guide" />
      </main>
    </div>
  );
}
